import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface IssueComment {
  id: string;
  issue_id: string;
  comment: string;
  created_by: string;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  attachments?: string[];
}

export const useIssueComments = (issueId?: string) => {
  const queryClient = useQueryClient();

  const { data: comments, isLoading } = useQuery({
    queryKey: ['issue-comments', issueId],
    queryFn: async () => {
      if (!issueId) return [];
      
      const { data, error } = await supabase
        .from('admin_issue_comments')
        .select('*')
        .eq('issue_id', issueId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data as IssueComment[];
    },
    enabled: !!issueId,
  });

  const addCommentMutation = useMutation({
    mutationFn: async (newComment: any) => {
      const { data, error } = await supabase
        .from('admin_issue_comments')
        .insert([newComment])
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-comments', issueId] });
      toast.success('Kommentar tilføjet!');
    },
    onError: (error) => {
      console.error('Error adding comment:', error);
      toast.error('Kunne ikke tilføje kommentar');
    },
  });

  const updateCommentMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<IssueComment> }) => {
      const { data, error } = await supabase
        .from('admin_issue_comments')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-comments', issueId] });
      toast.success('Kommentar opdateret!');
    },
    onError: (error) => {
      console.error('Error updating comment:', error);
      toast.error('Kunne ikke opdatere kommentar');
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('admin_issue_comments')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-comments', issueId] });
      toast.success('Kommentar slettet!');
    },
    onError: (error) => {
      console.error('Error deleting comment:', error);
      toast.error('Kunne ikke slette kommentar');
    },
  });

  return {
    comments: comments || [],
    isLoading,
    addComment: addCommentMutation.mutate,
    updateComment: updateCommentMutation.mutate,
    deleteComment: deleteCommentMutation.mutate,
  };
};
